import React from 'react'

import { DataTable } from '../../../components/Table/DataTable.react'
import { Column } from '../../../components/layout/Column.react'
import { Container } from '../../../components/layout/Container.react'
import { Money } from '../../../components/Money/Money.react'
import { Row } from '../../../components/layout/Row.react'
import { countryDate } from '../../../core/main/filter/country_date/filter'
import Authentication from '../../../main/factory/authentication'
import '../../i18next.react'
import { useTranslation } from 'react-i18next'

const CountryMoney = ({ amount }) => <><Money {...amount} />&nbsp;<span>{amount?.currency}</span></>

const MaybeCountryMoney = maybeAmount => {
  if (maybeAmount) {
    return <CountryMoney amount={maybeAmount} />
  }
  return null
}

const feesMap = {
  US: { includingTaxTitle: 'fees-with-tax', excludingTaxTitle: 'fees-ex-tax', classname: 'fees-with-tax' },
  CA: { includingTaxTitle: 'fees-with-tax', excludingTaxTitle: 'fees-ex-tax', classname: 'fees-with-tax' },
  GB: { includingTaxTitle: 'fees-with-vat', excludingTaxTitle: 'fees-ex-vat', classname: 'fees-with-vat' },
  FR: { includingTaxTitle: 'fees-with-vat', excludingTaxTitle: 'fees-ex-vat', classname: 'fees-with-vat' },
  IT: { includingTaxTitle: 'fees-with-vat', excludingTaxTitle: 'fees-ex-vat', classname: 'fees-with-vat' },
  ES: { includingTaxTitle: 'fees-with-vat', excludingTaxTitle: 'fees-ex-vat', classname: 'fees-with-vat' },
  AU: { includingTaxTitle: 'fees-with-gst', excludingTaxTitle: 'fees-ex-gst', classname: 'fees-with-gst' },
  NZ: { includingTaxTitle: 'fees-with-gst', excludingTaxTitle: 'fees-ex-gst', classname: 'fees-with-gst' }
}

const feesIncludingTaxTitle = (countryCode) => (feesMap[countryCode].includingTaxTitle)
const feesExcludingTaxTitle = (countryCode) => (feesMap[countryCode].excludingTaxTitle)
const feesClassname = (countryCode) => (feesMap[countryCode].classname)

const headers = ({ countryCode }) => [
  { title: 'store-id', width: 5 },
  { title: 'branch-name', width: 10 },
  { title: 'orders', width: 5 },
  { title: 'refunds', width: 10 },
  { title: 'order-amount', width: 10 },
  { title: 'refunds-amount', width: 10 },
  { title: 'orders-less-refunds', width: 15 },
  { title: feesExcludingTaxTitle(countryCode), width: 10 },
  { title: feesIncludingTaxTitle(countryCode), width: 10 },
  { title: 'net-amount-settled', width: 15 }
]

const columns = ({ countryCode }) => [
  {
    width: 5,
    extraClassNames: 'store',
    contentFunction: ({ storeId }) => storeId
  }, {
    width: 10,
    extraClassNames: 'branch',
    contentFunction: ({ storeName }) => storeName
  }, {
    width: 5,
    extraClassNames: 'orders',
    contentFunction: ({ orderCount }) => orderCount
  }, {
    width: 10,
    extraClassNames: 'refunds',
    contentFunction: ({ refundCount }) => refundCount
  }, {
    width: 10,
    extraClassNames: 'order-amount',
    contentFunction: ({ orderAmount }) => MaybeCountryMoney(orderAmount)
  }, {
    width: 10,
    extraClassNames: 'refund-amount',
    contentFunction: ({ refundAmount }) => {
      if (typeof refundAmount === 'string') {
        return refundAmount
      }
      return MaybeCountryMoney(refundAmount)
    }
  }, {
    width: 15,
    extraClassNames: 'orders-less-refunds',
    contentFunction: ({ orderLessRefundAmount }) => MaybeCountryMoney(orderLessRefundAmount)
  }, {
    width: 10,
    extraClassNames: feesClassname(countryCode),
    contentFunction: ({ transactionFeeExclGst }) => {
      if (transactionFeeExclGst) {
        return (
          <span
            className='tooltip tooltip-east tooltip-nowrap tooltip-fees-tax'
            data-tooltip={`${Money({ ...transactionFeeExclGst, decimalPlaces: 6 })} ${transactionFeeExclGst?.currency}`}
          >
            <CountryMoney amount={transactionFeeExclGst} />
          </span>
        )
      }
      return null
    }
  }, {
    width: 10,
    extraClassNames: feesClassname(countryCode),
    contentFunction: ({ transactionFeeInclGst }) =>
      <span
        className='tooltip tooltip-east tooltip-nowrap tooltip-fees-tax'
        data-tooltip={`${Money({ ...transactionFeeInclGst, decimalPlaces: 6 })} ${transactionFeeInclGst?.currency}`}
      >
        <CountryMoney amount={transactionFeeInclGst} />
      </span>
  }, {
    width: 15,
    extraClassNames: 'net-amount-settled',
    contentFunction: ({ netSettlementAmount }) =>
      <span
        className='tooltip tooltip-east tooltip-nowrap tooltip-net-settlement-amount'
        data-tooltip={`${Money({ ...netSettlementAmount, decimalPlaces: 6 })} ${netSettlementAmount?.currency}`}
      >
        <CountryMoney amount={netSettlementAmount} />
      </span>
  }
]

const settlementNgModule = 'merchant.application.main.factory.settlement'

export const ReconciliationByStoreSummary = ({ settlementId, $location, [settlementNgModule]: Settlement }) => {
  const merchantId = Authentication.merchantId()
  const countryCode = Authentication.countryCode()

  const source = $location.search().source
  const [isFetchingSummary, setIsFetchingSummary] = React.useState(false)
  const [reconciliationSummaryData, setReconciliationSummaryData] = React.useState({})

  const { t } = useTranslation('settlements')

  const loadSummaryData = () => {
    if (isFetchingSummary) {
      return
    }

    setIsFetchingSummary(true)

    const parameters = {
      settlementId,
      merchantId,
      source
    }

    Settlement.read(parameters, function (data) {
      setReconciliationSummaryData(data)
      setIsFetchingSummary(false)
    }, function () {
      setIsFetchingSummary(false)
    })
  }

  React.useEffect(loadSummaryData, [])

  return (
    <section className='settlement-detail'>
      <Container>
        <Row inset insetBreak>
          <Column width={100}>
            ({t('date')}:&nbsp;{countryDate(reconciliationSummaryData.settlementDate, { countryCode })},&nbsp;
            {t('total')}:&nbsp;<CountryMoney amount={reconciliationSummaryData.settlementAmount} />)
          </Column>
        </Row>
      </Container>
      <ReconciliationByStoreSummaryTable
        settlementId={settlementId}
        merchantId={merchantId}
        countryCode={countryCode}
        source={source}
        $location={$location}
        Settlement={Settlement}
        reconciliationSummaryData={reconciliationSummaryData}
      />
    </section>
  )
}

const ReconciliationByStoreSummaryTable = ({ settlementId, merchantId, countryCode, source, $location, Settlement, reconciliationSummaryData }) => {
  const ITEMS_PER_PAGE = 20

  const initialSearch = $location.search()
  const initialPage = initialSearch.page && initialSearch.page - 1

  const [totalItems, setTotalItems] = React.useState(0)
  const [currentPage, setCurrentPage] = React.useState(initialPage || 0)
  const [isFetching, setIsFetching] = React.useState(false)
  const [reconciliationByStoreData, setReconciliationByStoreData] = React.useState([])

  const { t } = useTranslation('settlements')

  const updateLocation = ({ page }) => {
    setCurrentPage(page)
  }

  const loadItemData = () => {
    if (isFetching) {
      return
    }

    setIsFetching(true)

    $location.search('page', currentPage + 1)

    const parameters = {
      settlementId,
      merchantId,
      source,
      offset: currentPage * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE
    }

    Settlement.readSummaryList(parameters, function ({ totalResults = 0, results = [] }) {
      setReconciliationByStoreData(results)
      setTotalItems(totalResults)
      setIsFetching(false)
    }, function () {
      setIsFetching(false)
    })
  }

  const totalRow = () => ({
    refundAmount: 'Total',
    orderLessRefundAmount: reconciliationSummaryData?.totalAmount || {},
    transactionFeeInclGst: reconciliationSummaryData?.totalMerchantFeesIncludingTax || {},
    netSettlementAmount: reconciliationSummaryData?.settlementAmount || {}
  })

  React.useEffect(loadItemData, [currentPage, settlementId, merchantId, source])

  const isLastPage = () => {
    return currentPage === (Math.ceil(totalItems / ITEMS_PER_PAGE) - 1)
  }

  const itemsAndMaybeTotal = () => {
    if (isLastPage()) {
      return reconciliationByStoreData.concat(totalRow())
    } else {
      return reconciliationByStoreData
    }
  }

  const translatedHeaders = (cc) => headers(cc).map(header => {
    return {
      ...header,
      title: t(header.title)
    }
  })

  return (
    <DataTable
      headers={translatedHeaders({ countryCode })}
      columns={columns({ countryCode })}
      updateLocation={updateLocation}
      data={itemsAndMaybeTotal()}
      totalItems={totalItems}
      limit={ITEMS_PER_PAGE}
      currentPage={currentPage}
    />
  )
}
